import React from "react";
import type {
  IndicatorProps,
  MenuProps,
  OptionProps,
  SingleValueProps,
} from "react-select";
import { components } from "react-select";
import styled, { useTheme } from "styled-components/macro";
import type { OptionType } from "../../types/types";
import { BoltIcon, SearchIcon } from "../Icons/Icons";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";

/**
 * This file exists to prevent needless duplication between `SelectBoxV2` and `AsyncSelectBox`
 */

const { ValueContainer, Placeholder, Option } = components;

const InterDiv = styled.div`
  font-family: "Inter";
  margin-top: ${({ theme }) => theme.resolve("spacing_sm")};
`;

export const CustomValueContainer = ({ children, ...props }: any) => {
  return (
    <ValueContainer {...props}>
      <InterDiv>
        <Placeholder
          {...props}
          isFocused={props.isFocused}
          style={{ marginTop: "-5px" }}
        >
          {props.selectProps.placeholder}
        </Placeholder>
        {React.Children.map(children, (child) =>
          child && child.type !== Placeholder ? child : null
        )}
      </InterDiv>
    </ValueContainer>
  );
};

export const CustomOption = (props: OptionProps<OptionType, false>) => {
  const isDefault =
    props.selectProps.setDefaultTerm &&
    props.options[0].value === props.data.value;
  return (
    <>
      {isDefault && (
        <Option {...props}>
          <strong>Default</strong>&nbsp;-&nbsp;{props.label}
        </Option>
      )}
      {!isDefault && <Option {...props}></Option>}
    </>
  );
};

export const CustomOptionWithStyledParens = (
  props: OptionProps<OptionType, false>
) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { data, label, selectProps, options } = props;

  const isDefault =
    selectProps.setDefaultTerm && options[0].value === data.value;

  const match = label.match(/^(.*?)(\s*\(.*\))$/);
  const mainLabel = match ? match[1] : label;
  const suffix = match ? match[2] : "";

  return (
    <Option {...props}>
      {isDefault && <strong>{t("Default")}</strong>}
      {isDefault && " - "}
      <span>{mainLabel}</span>
      {suffix && (
        <span style={{ color: theme.secondaryTextColor, marginLeft: 4 }}>
          {suffix}
        </span>
      )}
    </Option>
  );
};

export const DropdownIndicator = (props: IndicatorProps<OptionType, false>) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <SearchIcon />
      </components.DropdownIndicator>
    )
  );
};

export const AutofilledIndicator = (
  props: IndicatorProps<OptionType, false>
) => {
  const { innerProps, selectProps, ...rest } = props;
  const customInnerProps = {
    ...innerProps,
    onMouseDown: (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    },
    onTouchEnd: (e: React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
    },
  };

  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator
        {...rest}
        selectProps={selectProps}
        innerProps={customInnerProps}
      >
        <div
          className="auto-filled-indicator"
          data-tip="This field is auto-filled and cannot be cleared."
          data-for="auto-filled-tip"
        >
          <BoltIcon />
          <ReactTooltip id="auto-filled-tip" effect="solid" />
        </div>
      </components.DropdownIndicator>
    )
  );
};

export const Menu = (props: MenuProps<OptionType, false>) => (
  <InterDiv>
    <components.Menu {...props}>{props.children}</components.Menu>
  </InterDiv>
);

export const CustomSingleValueWithStyledParens = (
  props: SingleValueProps<OptionType>
) => {
  const theme = useTheme();

  const label = props.data.label;
  const match = label.match(/^(.*?)(\s*\(.*\))$/);
  const mainLabel = match ? match[1] : label;
  const suffix = match ? match[2] : "";

  return (
    <components.SingleValue {...props}>
      <span>{mainLabel}</span>
      {suffix && (
        <span style={{ color: theme.secondaryTextColor, marginLeft: 4 }}>
          {suffix}
        </span>
      )}
    </components.SingleValue>
  );
};
